/**
 * Author: Romain Hapi Sr.
 * Version: 1.0
 * Date: 2026-06-25
 * Project: Prenim Digital
 * Copyright: Prenim UK
 */
/**
 * Page « A propos » du site UK : mission, vision, valeurs, implantations.
 *
 * Les icones des valeurs vivent ici, dans `VALUE_ICONS`, et non dans les
 * fichiers de traduction : un pictogramme n'est pas du texte a traduire, et
 * l'association se fait par position dans la liste des valeurs.
 */
import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import SEO from "../components/SEO";
import MEDIA from "../lib/media";
import { ArrowRight, ShieldCheck, Heart, Target, BookOpen } from "lucide-react";

const VALUE_ICONS = [ShieldCheck, Heart, Target, BookOpen];

const About = () => {
  const { t, lang } = useLang();
  if (!t) return null;
  const meta = t.meta.pages.about;

  return (
    <main data-testid="about-page" className="pt-20">
      <SEO title={meta.title} description={meta.description} lang={lang} />

      {/* Hero */}
      <section className="relative bg-slate-950 text-white py-24 lg:py-32 overflow-hidden">
        <div
          className="absolute inset-0 opacity-25 bg-cover bg-center"
          style={{ backgroundImage: `url('${MEDIA.african_tech_team_2}')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 to-slate-950/60" />
        <div className="relative max-w-site mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-xs font-semibold uppercase tracking-widest text-cyan-400">
            {t.about.hero.eyebrow}
          </span>
          <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] max-w-3xl">
            {t.about.hero.title}
          </h1>
          <p className="mt-6 text-lg lg:text-xl text-slate-300 max-w-2xl leading-relaxed">
            {t.about.hero.subtitle}
          </p>
        </div>
      </section>

      {/* Mission / Vision */}
      <section className="py-24 lg:py-28">
        <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-8">
          <div className="rounded-2xl bg-slate-50 border border-slate-200 p-10">
            <span className="text-xs font-semibold uppercase tracking-widest text-blue-600">
              {t.about.mission.title}
            </span>
            <p className="mt-4 text-2xl font-bold text-slate-950 leading-tight">
              {t.about.mission.body}
            </p>
          </div>
          <div className="rounded-2xl bg-slate-950 text-white p-10 relative overflow-hidden">
            <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-cyan-400/20 blur-3xl" />
            <span className="text-xs font-semibold uppercase tracking-widest text-cyan-400 relative">
              {t.about.vision.title}
            </span>
            <p className="mt-4 text-2xl font-bold leading-tight relative">{t.about.vision.body}</p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section data-testid="about-values" className="py-24 lg:py-28 bg-slate-50">
        <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.about.values.map((v, i) => {
              const Icon = VALUE_ICONS[i] || ShieldCheck;
              return (
                <div
                  key={i}
                  data-testid={`about-value-${i}`}
                  className="bg-white rounded-2xl border border-slate-200 p-7 card-lift"
                >
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-cyan-50 text-cyan-600 mb-5">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-950 mb-2">{v.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{v.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section data-testid="about-timeline" className="py-24 lg:py-28">
        <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-950 mb-14">
            {t.about.timeline.title}
          </h2>
          <ol className="relative border-s-2 border-slate-200 ms-3">
            {t.about.timeline.items.map((item, i) => (
              <li key={i} data-testid={`timeline-item-${i}`} className="ms-8 mb-12 last:mb-0">
                <span className="absolute -start-[11px] flex items-center justify-center h-5 w-5 rounded-full bg-blue-600 ring-4 ring-white" />
                <span className="text-xs font-semibold uppercase tracking-widest text-blue-600">
                  {item.date}
                </span>
                <h3 className="text-2xl font-bold text-slate-950 mt-1.5 mb-2">{item.title}</h3>
                <p className="text-slate-600 leading-relaxed max-w-2xl">{item.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Positioning */}
      <section className="py-24 lg:py-28 bg-slate-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-slate opacity-10" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
            {t.about.positioning.title}
          </h2>
          <p className="text-lg text-slate-300 leading-relaxed">{t.about.positioning.body}</p>
          <Link
            to="/services"
            data-testid="about-cta"
            className="inline-flex items-center gap-2 mt-10 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-semibold px-6 py-3 transition-colors"
          >
            {t.common.all_services}
            <ArrowRight className="h-4 w-4 rtl-flip" />
          </Link>
        </div>
      </section>
    </main>
  );
};

export default About;
